import { MobileFilter } from "src/molecules/MobileFilter";
import {
  MobileActiveFilters,
  MobileFilterOptionButton,
} from "./MobileFilters.styled";
import { msg } from "@lingui/macro";
import { useCallback, useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import { ArrowRightIcon } from "src/icons";
import { Text } from "@otrium/atoms";
import { MobileBrandFilter } from "src/molecules/MobileFilter/MobileBrandFilter";
import { MobileRangeFilter } from "src/molecules/MobileFilter/MobileRangeFilter";
import { Price } from "src/atoms/Price";
import { theme } from "src/theme";
import { useSegment } from "src/hooks/useSegment";
import { FACET_ICONS } from "./facet-icons";
import type { ListingFacet } from "src/types/adapters/listing.type";
import { FACET_LABELS } from "src/utils/search-provider/constants";

type MobileFilterOptionProps = {
  facet: ListingFacet;
};

function MobileFilterOption({
  facet,
}: MobileFilterOptionProps): JSX.Element | null {
  const { _ } = useLingui();
  const [isOpen, setOpen] = useState(false);
  const { segmentProductListFilterOpened } = useSegment();
  const { title, icon } = useMemo(
    () => ({ title: FACET_LABELS[facet.key], icon: FACET_ICONS[facet.key] }),
    [facet.key]
  );

  const { selectedCount, refinementsLabel, hasFilters } = useMemo(() => {
    if (facet.type === "range") {
      const hasSelection = !!facet.data.currentValue;

      return {
        selectedCount: hasSelection ? 1 : 0,
        refinementsLabel: hasSelection ? (
          <>
            <Price price={facet.data.currentValue?.min || 0} /> -{" "}
            <Price price={facet.data.currentValue?.max || 0} />
          </>
        ) : null,
        hasFilters: hasSelection,
      };
    }

    const selected = facet.data.filter((option) => option.selected);
    const labels = selected.map(
      (option) => option.label.split("||").at(1) || option.label
    );

    return {
      selectedCount: selected.length,
      refinementsLabel: labels.join(", "),
      hasFilters: selected.length > 0,
    };
  }, [facet]);

  const canRefine = facet.type === "range" || facet.data.length > 0;

  const toggleOpen = useCallback(() => {
    setOpen((prevState) => {
      if (!prevState) {
        void segmentProductListFilterOpened(facet.key);
      }

      return !prevState;
    });
  }, [facet.key, segmentProductListFilterOpened]);

  if (!canRefine) {
    return null;
  }

  return (
    <>
      <MobileFilterOptionButton onClick={toggleOpen} disabled={!canRefine}>
        {icon}
        <Text minWidth="fit-content" color={theme.colors.tone.black}>
          {`${_(title)}${hasFilters ? ` (${selectedCount})` : ""}`}
        </Text>
        {hasFilters ? (
          <MobileActiveFilters>{refinementsLabel}</MobileActiveFilters>
        ) : null}
        <ArrowRightIcon />
      </MobileFilterOptionButton>

      {facet.key === "brands" ? (
        <MobileBrandFilter
          title={title}
          facet={facet}
          isOpen={isOpen}
          searchPlaceholder={_(msg`Search brand`)}
          toggleOpen={toggleOpen}
          showSearch
        />
      ) : null}

      {facet.key === "price" ? (
        <MobileRangeFilter
          title={title}
          facet={facet}
          isOpen={isOpen}
          toggleOpen={toggleOpen}
        />
      ) : null}

      {facet.key !== "brands" && facet.key !== "price" ? (
        <MobileFilter
          title={title}
          facet={facet}
          isOpen={isOpen}
          toggleOpen={toggleOpen}
        />
      ) : null}
    </>
  );
}

export { MobileFilterOption };
